import { useGetApPayeesInfinite } from '@/context/services/doc_instructions/DocInstructions.service';
import { PREFETCH_THRESHOLD } from '@/features/ap_payees/components/DocInstructionMenu/Constants';
import { PayeeListControls } from '@/features/ap_payees/components/DocInstructionMenu/types';
import { getPageData } from '@/utils/getPageData';
import { SearchApPayeeQuery, SearchedApPayee } from 'ap-openapi';
import { useCallback, useMemo, useState } from 'react';

/**
 * Hook to manage a list of payees with pagination and preview functionality
 * @param payees - Optional pre-loaded list of payees
 * @param payeeQuery - Query parameters for searching payees
 * @returns Controls for navigating and managing the payee list
 */
export const useGetPayeeList = (
  payees: SearchedApPayee[],
  payeeQuery: SearchApPayeeQuery
): PayeeListControls => {
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);

  const { apiData, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetApPayeesInfinite(payeeQuery);

  // Use either provided payees or API data
  const payeeList = useMemo(() => {
    if (payees?.length > 0) return payees;
    return getPageData(apiData, 'ap_payees');
  }, [apiData, payees]);

  // Calculate pagination info
  const pagination = useMemo(
    () => apiData?.pages?.[0]?.data.pagination,
    [apiData]
  );

  const totalPayees = useMemo(
    () => (payees?.length > 0 ? payees.length : pagination?.total_records || 0),
    [pagination, payees]
  );

  // Navigation controls
  const hasPreviousPayee = currentPreviewIndex > 0;
  const hasNextPayee = currentPreviewIndex < totalPayees - 1;

  const loadMorePayeeList = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  const onPreviewPreviousPayee = useCallback(() => {
    if (!hasPreviousPayee) return;
    setCurrentPreviewIndex(currentPreviewIndex - 1);
  }, [currentPreviewIndex, hasPreviousPayee]);

  const onPreviewNextPayee = useCallback(() => {
    if (!hasNextPayee) return;
    if (currentPreviewIndex === payeeList?.length - PREFETCH_THRESHOLD) {
      loadMorePayeeList();
    }
    setCurrentPreviewIndex((prev) => prev + 1);
  }, [currentPreviewIndex, hasNextPayee, loadMorePayeeList, payeeList?.length]);

  const currentPreviewPayee = useMemo(() => {
    return payeeList?.[currentPreviewIndex];
  }, [currentPreviewIndex, payeeList]);

  return {
    payeeList,
    isFetchingNextPage,
    loadMorePayeeList,
    hasPreviousPayee,
    hasNextPayee,
    onPreviewPreviousPayee,
    onPreviewNextPayee,
    currentPreviewIndex,
    totalPayees,
    currentPreviewPayee,
  };
};
